import { toast } from "@devhop/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  FeeRuleCreateInput,
  FeeRuleUpdateInput,
  FeeType,
} from "@/modules/fees/domain/contracts";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { feesApi } from "./client";

export const feesKeys = {
  all: ["fees"] as const,
  list: (feeType: FeeType, q: Partial<OffsetPageQueryDTO> & { q?: string }) =>
    ["fees", "list", feeType, q] as const,
};

export function useFeeRulesQuery(
  feeType: FeeType,
  query: Partial<OffsetPageQueryDTO> & { q?: string } = {},
) {
  const params = {
    feeType,
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
    sort: query.sort,
    filters: query.filters,
    q: query.q,
  };
  return useQuery({
    queryKey: feesKeys.list(feeType, params),
    queryFn: () => feesApi.list(params),
  });
}

export function useCreateFeeRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: FeeRuleCreateInput) => feesApi.create(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: feesKeys.all });
      toast.success("ເພີ່ມກົດຄ່າທຳນຽມສຳເລັດ");
    },
  });
}

export function useUpdateFeeRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: FeeRuleUpdateInput }) =>
      feesApi.update(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: feesKeys.all });
      toast.success("ແກ້ໄຂກົດຄ່າທຳນຽມສຳເລັດ");
    },
  });
}

export function useDeleteFeeRule() {
  const qc = useQueryClient();
  const base = useMutation({
    mutationFn: (id: string) => feesApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: feesKeys.all });
    },
  });
  const run = (id: string) =>
    new Promise<void>((resolve, reject) => {
      base.mutate(id, {
        onSuccess: () => resolve(),
        onError: (e) => reject(e),
      });
    });
  return { ...base, run };
}
